// Historia 40 — Restrict Order and FabricationOrder editing by status permissions (jupiter slice).
//
// Shared, pure gating helpers used by Order.jsx / LineOrder.jsx / FabricationOrder.jsx so the
// "can this row/action be edited given the current profile" logic is not duplicated across the
// three screens (S25, S26, S36).
//
// Two different mechanisms, per the Hard Spec (01-historia.md, D9/E1):
//  - Order status permissions are DYNAMIC (one `ORDER_STATUS_PERM_<code>` per configurable
//    OrderStatus). The front never checks a dynamic code as an authority: it relies on the
//    already-filtered catalog the backend returns from `simpleOrderStatuses` (S24) and only
//    checks ROW MEMBERSHIP in that catalog (`isOrderRowStatusAllowed`).
//  - FabricationOrder status permissions are FIXED (exactly 4: FO_STATUS_PERM_PENDING/PLANNED/
//    LAUNCHED/CLOSED, one per FabricationOrderGlobalStatusEnum value). Being a small, known,
//    static set — not one dynamically minted per row of configurable data — checking them as
//    ordinary authorities via `isUserAuthorized` is the same pattern already used everywhere else
//    in this codebase (e.g. `EDIT_ORDERS`), not an instance of the dynamic-code anti-pattern S24
//    warns against.

/**
 * S25/S26 — Order: whether a given order status is inside the backend-filtered catalog
 * (`simpleOrderStatuses`). Fail-safe: any missing/malformed input denies (returns false).
 *
 * @param {{id?: number|string}|null|undefined} status The order's current status (row.status)
 * @param {Array<{id: number|string}>} allowedOrderStatuses Catalog already filtered by the backend
 * @returns {boolean}
 */
export const isOrderStatusAllowed = (status, allowedOrderStatuses) => {
  if (!status || status.id === undefined || status.id === null) {
    return false
  }
  if (!Array.isArray(allowedOrderStatuses) || allowedOrderStatuses.length === 0) {
    return false
  }
  return allowedOrderStatuses.some((allowed) => allowed && allowed.id === status.id)
}

/**
 * S25/S36 — FabricationOrder: whether the profile owns the fixed permission for a given status of
 * the FabricationOrderGlobalStatusEnum (PENDING/PLANNED/LAUNCHED/CLOSED).
 *
 * @param {(permissionCode: string) => boolean} isUserAuthorized From useAuthorization()
 * @param {string} status One of PENDING/PLANNED/LAUNCHED/CLOSED
 * @returns {boolean}
 */
export const isFabricationOrderStatusAllowed = (isUserAuthorized, status) => {
  if (typeof isUserAuthorized !== 'function' || !status) {
    return false
  }
  return isUserAuthorized('FO_STATUS_PERM_' + status)
}

/**
 * S36 — whether a single FabricationOrder can transition from `currentStatus` to `targetStatus`:
 * requires permission on BOTH ends (RN17/CA12), and `PLANNED` is never offered as an interactive
 * destination (E1) regardless of authorities.
 *
 * @param {(permissionCode: string) => boolean} isUserAuthorized
 * @param {string} currentStatus
 * @param {string} targetStatus
 * @returns {boolean}
 */
export const canTransitionFabricationOrderStatus = (isUserAuthorized, currentStatus, targetStatus) => {
  if (targetStatus === 'PLANNED') {
    return false
  }
  return isFabricationOrderStatusAllowed(isUserAuthorized, currentStatus) &&
    isFabricationOrderStatusAllowed(isUserAuthorized, targetStatus)
}

/**
 * S36 — bulk variant: a multi-row status change action is only enabled when EVERY selected row can
 * individually transition to `targetStatus` (mirrors the backend's atomic all-or-nothing gate,
 * E3/E_BULK_STATE_MIXED). An empty selection is treated as not-actionable.
 *
 * @param {(permissionCode: string) => boolean} isUserAuthorized
 * @param {Array<{status?: string}>} rows Selected FabricationOrder rows
 * @param {string} targetStatus
 * @returns {boolean}
 */
export const canBulkTransitionFabricationOrderStatus = (isUserAuthorized, rows, targetStatus) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return false
  }
  return rows.every((row) => canTransitionFabricationOrderStatus(isUserAuthorized, row?.status, targetStatus))
}

/**
 * S36 — gate for the bulk action as the generic table actually models a selection:
 * `{ rows, allSelected }`. Two cases, one entry point:
 *  - explicit rows: delegates to `canBulkTransitionFabricationOrderStatus` (every row must own its
 *    current status and the destination).
 *  - `allSelected` (select-all across pages): the table empties `rows`, so the current statuses of
 *    the selection are unknowable on the front. Gating on the empty list would disable the action
 *    forever, so only the destination is checked here (`PLANNED` still excluded, E1) and Sun's
 *    atomic bulk gate (E_BULK_STATE_MIXED) rejects a selection holding a non-owned status.
 *
 * @param {(permissionCode: string) => boolean} isUserAuthorized
 * @param {{rows?: Array<{status?: string}>, allSelected?: boolean}} selection
 * @param {string} targetStatus
 * @returns {boolean}
 */
export const canBulkTransitionFabricationOrderSelection = (isUserAuthorized, selection, targetStatus) => {
  if (selection?.allSelected) {
    return targetStatus !== 'PLANNED' && isFabricationOrderStatusAllowed(isUserAuthorized, targetStatus)
  }
  return canBulkTransitionFabricationOrderStatus(isUserAuthorized, selection?.rows || [], targetStatus)
}

/**
 * Historia 65-skip-workflow-jobs-of-a-planned-fo — S31 (RN17/D7): whether a FabricationOrder
 * admits execution (skip/reactivate can be offered). The fixed FabricationOrderGlobalStatusEnum
 * only has 4 values (PENDING/PLANNED/LAUNCHED/CLOSED) — there is no separate "cancelled" status —
 * so CLOSED is the only terminal, non-operable status. Fails safe (not operable) on missing/
 * malformed input.
 *
 * @param {string} status One of PENDING/PLANNED/LAUNCHED/CLOSED
 * @returns {boolean}
 */
export const isFabricationOrderOperable = (status) => {
  if (!status) {
    return false
  }
  return status !== 'CLOSED'
}
